import { useEffect } from 'react';

import { sendAnalyticsWarning } from 'features/analytics/helpers';
import { useAppDispatch } from 'features/app/hooks';
import { Originator } from 'features/sip/enums';
import type { RtcConnection } from 'features/sip/interfaces';
import { SipSessionEventBus } from 'features/sip/services';

/**
 * Listens for JsSIP `hold` events (fired when the remote endpoint sends
 * `a=sendonly or a=inactive`). JsSIP's internal `_setLocalMediaStatus()` disables video
 * sender tracks during hold — this hook immediately re-enables them so
 * the interpreter's camera stays active.
 *
 * We do this because if an `unhold` later arrives and the track is re-disabled, that's acceptable.
 * However, in many edge cases the `unhold` never arrives; when that happens the interpreter can
 * end up sending blank video to the endpoint. This guard re-enables the sender track to prevent that.
 */
export const useTrackGuard = (
  rtcConnection: RtcConnection | null,
  isPrivacyEnabled: boolean
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!rtcConnection) {
      return;
    }

    const subscription = SipSessionEventBus.hold$.subscribe((event) => {
      if (event.originator !== Originator.REMOTE) {
        return;
      }

      // JsSIP has already set sender.track.enabled = false by the time
      // this event fires. Re-enable the video sender track immediately.
      const videoSender = rtcConnection
        .getSenders()
        .find((s) => s.track?.kind === 'video');

      const track = videoSender?.track;

      if (!track || track.readyState === 'ended') {
        return;
      }

      if (!track.enabled) {
        console.warn(
          'Track guard: re-enabling video sender track after remote hold'
        );
        dispatch(
          sendAnalyticsWarning({
            name: 'Track Guard Hold Re-enable',
            event: {
              trackId: track.id,
              readyState: track.readyState,
              originator: event.originator,
            },
          })
        );
        track.enabled = true;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [rtcConnection, isPrivacyEnabled, dispatch]);
};
